import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Wrench, Truck, Briefcase, CreditCard, Check, ArrowRight, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { PORTAL_LINKS } from '../constants/site';

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const services = [
    {
      id: 'equipment',
      icon: Wrench,
      title: 'Equipment Finance',
      tagline: 'Business Finance',
      description: 'Fund machinery and equipment with repayment structures aligned to business cash flow and asset use.',
      ctaPath: '/business-finance',
      features: [
        'New and used equipment accepted',
        'Flexible terms from 12 to 84 months',
        'Chattel mortgage and lease options',
        'Tax-effective financing structures',
        'Quick approvals — often within 24 hours, depending on the applicant, the lender’s credit criteria and the type of loan',
        'No deposit options available'
      ],
      industries: ['Construction', 'Manufacturing', 'Agriculture', 'Healthcare', 'Hospitality', 'Mining']
    },
    {
      id: 'vehicles',
      icon: Truck,
      title: 'Vehicle Finance',
      tagline: 'Business Finance',
      description: 'Finance commercial vehicles, utes, vans, and trucks with lender options that match your operating profile.',
      ctaPath: '/business-finance',
      features: [
        'Trucks, utes, vans and trailers',
        'New and used vehicles welcome',
        'Fleet finance packages',
        'Novated lease options',
        'Competitive rates across all vehicle types',
        'Finance for specialised and modified vehicles'
      ],
      industries: ['Transport & Logistics', 'Trades', 'Delivery Services', 'Agriculture', 'Mining', 'Construction']
    },
    {
      id: 'business-loans',
      icon: Briefcase,
      title: 'Business Loans',
      tagline: 'Business Finance',
      description: 'Access funding for growth, working capital, and strategic business opportunities with clear lender matching.',
      ctaPath: '/business-finance',
      features: [
        'Loans from $10,000 to $5 million+',
        'Terms from 6 months to 5 years',
        'Secured and unsecured options',
        'Fast approval process',
        'No early repayment penalties',
        'Fixed and variable rate options'
      ],
      industries: ['All Industries', 'Retail', 'Professional Services', 'E-commerce', 'Hospitality', 'Healthcare']
    },
    {
      id: 'line-of-credit',
      icon: CreditCard,
      title: 'Lines of Credit',
      tagline: 'Business Finance',
      description: 'Use a revolving credit facility to manage short-term cash flow without over-borrowing up front.',
      ctaPath: '/business-finance',
      features: [
        'Revolving credit facility',
        'Interest only on drawn funds',
        'Ongoing access to approved limit',
        'Perfect for cash flow management',
        'No redraw fees',
        'Available 24/7 when you need it'
      ],
      industries: ['Seasonal Businesses', 'Project-based Work', 'Growing Enterprises', 'Importers/Exporters', 'Any Business']
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Services | Car Loans, Personal Loans & Business Finance"
        description="Explore Flash Funding service pathways for Car Loans, Personal Loans, and Business Finance including equipment, loans, cash flow, and lines of credit."
        keywords="equipment finance Australia, vehicle finance, business loans, lines of credit, truck finance, machinery finance, chattel mortgage, commercial lending, fleet finance"
        canonical="/services"
      />
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-heading font-black text-5xl sm:text-6xl text-white uppercase tracking-tight leading-none mb-6">
              Finance<br />
              <span className="gold-gradient-text">Services</span>
            </h1>
            <p className="font-body text-lg sm:text-xl text-white/70">
              Choose a clear pathway: Car Loans, Personal Loans, or Business Finance. We match you to the right lender and handle the process.
            </p>

            <div className="mt-7 flex flex-wrap justify-center gap-3" data-testid="services-hero-cta-row">
              <Link to="/car-loans" data-testid="services-hero-car-link">
                <Button className="rounded-full bg-purple-600 hover:bg-purple-500 text-white text-xs uppercase tracking-[0.13em] font-bold px-6 py-5" data-testid="services-hero-car-cta">
                  Check Your Options
                </Button>
              </Link>
              <Link to="/commercial-car-loans" data-testid="services-hero-commercial-car-link">
                <Button variant="outline" className="rounded-full border-purple-400/40 bg-navy-900/60 text-white hover:bg-purple-500/15 text-xs uppercase tracking-[0.13em] font-bold px-6 py-5" data-testid="services-hero-commercial-car-cta">
                  Check Your Options
                </Button>
              </Link>
              <Link to="/personal-loans" data-testid="services-hero-personal-link">
                <Button variant="outline" className="rounded-full border-purple-400/40 bg-navy-900/60 text-white hover:bg-purple-500/15 text-xs uppercase tracking-[0.13em] font-bold px-6 py-5" data-testid="services-hero-personal-cta">
                  See What You Can Get Approved For
                </Button>
              </Link>
              <Link to="/business-finance" data-testid="services-hero-business-link">
                <Button variant="outline" className="rounded-full border-purple-400/40 bg-navy-900/60 text-white hover:bg-purple-500/15 text-xs uppercase tracking-[0.13em] font-bold px-6 py-5" data-testid="services-hero-business-cta">
                  Check Your Options
                </Button>
              </Link>
            </div>

            <p className="font-body text-sm text-white/60 mt-4" data-testid="services-hero-route-priority-note">
              Start with your service page, then complete your application in our secure portal when you&apos;re ready.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      {services.map((service, index) => (
        <section 
          key={service.id}
          id={service.id}
          className={`py-20 lg:py-28 ${index % 2 === 0 ? 'bg-navy-800' : 'bg-navy-900'} scroll-mt-24`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-lg bg-gold-500/10 flex items-center justify-center">
                    <service.icon size={28} className="text-gold-500" />
                  </div>
                  <span className="font-mono text-gold-500 text-sm uppercase tracking-wider">{service.tagline}</span>
                </div>
                
                <h2 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight mb-6">
                  {service.title}
                </h2>
                
                <p className="font-body text-white/70 text-lg mb-8">
                  {service.description}
                </p>

                <div className="mb-8">
                  <h4 className="font-heading font-bold text-lg text-white uppercase mb-4">Key Features</h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                        <span className="font-body text-white/70 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link to={service.ctaPath} data-testid={`services-section-primary-link-${service.id}`}>
                  <Button 
                    data-testid={`apply-${service.id}-btn`}
                    className="gold-gradient text-black font-heading font-bold uppercase tracking-wider px-8 py-6 hover:scale-105 transition-transform gold-glow"
                  >
                    Check Your Options
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
              </div>

              {/* Card */}
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="bg-navy-700 border border-white/5 p-8 lg:p-10">
                  <h4 className="font-heading font-bold text-xl text-white uppercase mb-6">
                    Industries We Serve
                  </h4>
                  <div className="flex flex-wrap gap-3">
                    {service.industries.map((industry) => (
                      <span 
                        key={industry}
                        className="px-4 py-2 bg-navy-600 border border-white/10 font-body text-white/70 text-sm"
                      >
                        {industry}
                      </span>
                    ))}
                  </div>

                  <div className="mt-8 pt-8 border-t border-white/10">
                    <div className="flex items-center gap-4">
                      <Zap size={24} className="text-gold-500" />
                      <div>
                        <p className="font-heading font-bold text-white uppercase">Lender-Matched Process</p>
                        <p className="font-body text-white/60 text-sm">We handle the process so you can move from option to outcome faster.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-20 lg:py-28 gold-gradient">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-black text-4xl sm:text-5xl text-black uppercase tracking-tight mb-6">
            Not sure which finance path fits?
          </h2>
          <p className="font-body text-black/70 text-lg mb-10 max-w-2xl mx-auto">
            Start with your service type first, then move to the application form when ready.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-testid="services-final-cta-row">
            <Link to="/business-finance" data-testid="services-final-primary-link">
              <Button 
                data-testid="services-final-primary-cta"
                className="bg-navy-900 text-white hover:bg-navy-800 font-heading font-bold text-xs uppercase tracking-[0.14em] px-8 py-6"
              >
                Check Your Options
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <a href={PORTAL_LINKS.GENERIC} data-testid="services-final-secondary-link">
              <Button 
                variant="outline"
                data-testid="services-final-secondary-cta"
                className="border-navy-900/40 text-navy-900 hover:bg-navy-900/10 font-heading font-bold text-xs uppercase tracking-[0.14em] px-8 py-6"
              >
                See What You Can Get Approved For
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
